/**
 * MorePage (/more) — the mobile "More" tab (Slack-style, Sep 2026): every app
 * the user can open, grouped like the desktop sidebar, one tappable row each.
 * The bottom bar keeps only Home, two pinned tools, Activity and More.
 */
import { Link } from 'react-router-dom';
import { ChevronRight, Search, UserRound } from 'lucide-react';
import { Icon } from '../design/Icon';
import { PageHeader } from '../components/PageHeader';
import { useSharedAppNav, ICONS, type NavItem } from '../components/useAppNav';
import { navDescription } from '../components/navDescriptions';
import { openCommandPalette } from '../components/CommandPalette';
import { pageFrame } from '../design/ui';

function Row({ item }: { item: NavItem }) {
  const desc = navDescription(item.to);
  return (
    <li>
      <Link to={item.to} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors duration-fast">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-fg-muted">
          <Icon as={item.icon} size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-fg">{item.label}</span>
          {desc && <span className="block text-xs text-fg-muted truncate">{desc}</span>}
        </span>
        {!!item.badge && <span className="w-2 h-2 rounded-full bg-brand-solid" aria-label={`${item.badge} open`} />}
        <Icon as={ChevronRight} className="text-fg-faint" />
      </Link>
    </li>
  );
}

export function MorePage() {
  const { navGroups, adminItems, isManager } = useSharedAppNav();
  const groups = [
    ...(isManager ? [{ label: 'You', items: [{ to: '/my-team', label: 'My Team', icon: ICONS.fte }] as NavItem[] }] : []),
    ...navGroups,
    ...(adminItems.length > 0 ? [{ label: 'Admin', items: adminItems }] : []),
  ];
  return (
    <main className={`${pageFrame} space-y-6`}>
      <PageHeader title="More" summary="Every app you have access to." />
      <button
        type="button"
        onClick={openCommandPalette}
        className="w-full flex items-center gap-2 h-10 px-3 rounded-lg border border-line bg-surface text-sm text-fg-muted"
      >
        <Icon as={Search} /> Search apps, pages and people
      </button>
      {groups.map((g) => (
        <section key={g.label} className="space-y-2">
          <h2 className="text-2xs font-semibold uppercase text-fg-faint">{g.label}</h2>
          <ul className="rounded-lg border border-line bg-surface divide-y divide-line overflow-hidden">
            {g.items.map((it) => <Row key={it.to} item={it} />)}
          </ul>
        </section>
      ))}
      <section className="space-y-2">
        <h2 className="text-2xs font-semibold uppercase text-fg-faint">Account</h2>
        <ul className="rounded-lg border border-line bg-surface divide-y divide-line overflow-hidden">
          <Row item={{ to: '/guide', label: 'Guide', icon: ICONS.guide }} />
          <Row item={{ to: '/profile', label: 'Profile & settings', icon: UserRound }} />
        </ul>
      </section>
    </main>
  );
}
